/**
 * T-163-001 — Smart Document Reader tier guard (US-163 / F-180, Enterprise-only).
 *
 * Route-level entitlement check, parity with the Evidence Safe gate (T-162-001 /
 * F-179): reads the tenant's plan tier from LP GET /api/subscription/current and
 * evaluates access BEFORE rendering the Smart Document Reader route. Fail-closed:
 * if the tier cannot be verified, access is denied. Super Admin bypass included.
 *
 * Scope (per dispatch): FE gate + upgrade modal only. The document-processing
 * Lambda activity (T-163-003, buffy) stays server-side; this guard fronts the
 * FE route surface.
 */
import { adminApi } from '../../services/api';
import { isSuperAdminSession } from '../../utils/buildUserFromToken';

export type SmartReaderFeature = 'smart-document-reader';

/** Route path → feature name. */
export const SMART_READER_ROUTES: Record<string, SmartReaderFeature> = {
    '/texas/smart-reader': 'smart-document-reader',
};

export const SMART_READER_TIER = 'enterprise';

export interface SmartReaderGateResponse {
    allowed: boolean;
    denied: boolean;
    feature: SmartReaderFeature;
    tier: string | null;
    reason: string;
}

export function isSmartReaderTier(tier: string | null | undefined): boolean {
    return typeof tier === 'string' && tier.trim().toLowerCase() === SMART_READER_TIER;
}

/** Pure evaluator — the T-163-001 gate decision. */
export function evaluateSmartReaderGate(
    tier: string | null | undefined,
    feature: SmartReaderFeature = 'smart-document-reader'
): SmartReaderGateResponse {
    const normalized = tier == null || String(tier).trim() === '' ? null : String(tier).trim().toLowerCase();
    const allowed = isSmartReaderTier(normalized);
    return {
        allowed,
        denied: !allowed,
        feature,
        tier: normalized,
        reason: allowed
            ? 'Enterprise tier verified.'
            : `Smart Document Reader requires the Enterprise tier — current plan: ${normalized || 'unknown'}.`,
    };
}

/** Read the signed-in tenant's tier (LP contract: data.tier). */
export async function fetchTenantTier(): Promise<string | null> {
    const res = await adminApi.get('api/subscription/current');
    const data = res?.data?.data ?? res?.data ?? {};
    const tier = data.tier ?? data.plan_tier ?? null;
    return tier == null ? null : String(tier);
}

/** Async gate entry point used by the route wrapper. Fail-closed on errors.
 *  Super Admin bypass: staff accounts are unrestricted. */
export async function checkSmartReaderAccess(
    feature: SmartReaderFeature = 'smart-document-reader'
): Promise<SmartReaderGateResponse> {
    if (isSuperAdminSession()) {
        return {
            allowed: true,
            denied: false,
            feature,
            tier: null,
            reason: 'Super Admin — unrestricted access to all features.',
        };
    }
    try {
        const tier = await fetchTenantTier();
        return evaluateSmartReaderGate(tier, feature);
    } catch {
        return {
            allowed: false,
            denied: true,
            feature,
            tier: null,
            reason: 'Could not verify your plan tier — access blocked until verification succeeds (fail-closed).',
        };
    }
}